import { useEffect, useRef } from 'react';

import { useWorkflowContext } from '../state/WorkflowContext';

/**
 * Hook to measure and register a node's height for the tree layout algorithm.
 * Uses ResizeObserver to automatically update when the node's size changes.
 *
 * @param nodeId - The unique identifier of the node
 * @returns A ref to attach to the node's root element
 */
export const useRegisterNodeHeight = <T extends HTMLElement>(nodeId: string) => {
  const { registerNodeHeight } = useWorkflowContext();
  const nodeReference = useRef<T>(null);

  useEffect(() => {
    const element = nodeReference.current;
    if (!element || !registerNodeHeight) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const height = entry.contentRect.height;
        if (height > 0) {
          registerNodeHeight(nodeId, height);
        }
      }
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [nodeId, registerNodeHeight]);

  return nodeReference;
};

